/**
 * Role-based landing page resolver.
 *
 * Single source of truth used by every login page + the header account link
 * so role redirects stay consistent across the platform.
 *
 *   admin           → /admin/bookings   (canonical admin dashboard; /admin alias resolves here)
 *   lab_technician  → /admin/lab        (primary lab workspace)
 *   member (trade)  → /trade
 *   property_manager (resolved server-side) → /property-manager
 *   customer        → /customer
 *   anything else   → /customer         (graceful fallback)
 */
export function landingForRole(role) {
  switch (role) {
    case "admin":          return "/admin/bookings";
    case "lab_technician": return "/admin/lab";
    case "member":         return "/trade";
    case "partner":        return "/partner/dashboard";
    case "technician":     return "/field/today";
    case "customer":       return "/customer";
    default:               return "/customer";
  }
}

/**
 * Resolve landing page for a user object. Phase 2L-A112 — a registered
 * property manager (`role=customer` with a matching `partners` row of
 * `partner_type=property_manager`) should land on `/property-manager`,
 * NOT the homeowner dashboard at `/customer` (which surfaces the
 * "Join the Passport" upsell card meant for residential customers).
 *
 * The auth payload now carries `is_property_manager` resolved at login
 * time, so the redirect is a single property-read with no extra
 * round-trip.
 */
export function landingForUser(user) {
  if (user?.is_property_manager) return "/property-manager";
  // Aug 2026 — non-PWS partners (Retail Stockist / kit purchasers) must not
  // land on the PWS-franchisee-only dashboard, which 403s for them.
  if (user?.role === "partner" && user?.partner_type && user.partner_type !== "pws_franchisee") {
    const caps = user.partner_capabilities || {};
    if (caps.can_retail) return "/trade/retail";
    if (caps.can_purchase_kits) return "/partner/kit-orders";
    // Oct 2026 fix — generic trade partners (conveyancer, buyer_agent,
    // etc.) with can_refer only (or no capability yet) used to fall
    // through to landingForRole() → "/partner/dashboard", which is
    // PWS-franchisee-only and 403s for them. /partner-hub is the
    // correct generic landing page — it shows their attributed
    // referral/booking link when can_refer is on, or a friendly
    // "no capabilities yet" message otherwise.
    return "/partner-hub";
  }
  return landingForRole(user?.role);
}
